import { z } from "zod";
import { idSchema } from "./ids.js";

export const instantSchema = z.iso.datetime({ offset: false, precision: 3 });

export const moneySchema = z.strictObject({
  currency: z.string().regex(/^[A-Z]{3}$/),
  amount_decimal: z.string().regex(/^-?\d+(\.\d+)?$/),
});
export type Money = z.infer<typeof moneySchema>;

export const actorSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("user"), user_id: idSchema("usr") }),
  z.strictObject({ kind: z.literal("machine_credential"), credential_id: idSchema("mcr") }),
  z.strictObject({ kind: z.literal("process"), name: z.string().min(1) }),
]);
export type Actor = z.infer<typeof actorSchema>;

export const auditActorSchema = z.discriminatedUnion("kind", [
  ...actorSchema.options,
  z.strictObject({ kind: z.literal("anonymous"), verified_email: z.email() }),
]);

export const DATE_PRECISIONS = ["year", "month", "day", "minute", "second"] as const;
export const DATE_BASES = [
  "publisher_metadata",
  "official_record",
  "page_byline",
  "feed",
  "publication_proxy",
  "editor",
  "reference_data",
  "news_supplier",
  "secondary_report",
] as const;
export type DateBasis = (typeof DATE_BASES)[number];

export const sourceDateSchema = z.strictObject({
  value: z.string().min(4),
  precision: z.enum(DATE_PRECISIONS),
  timezone: z.string().nullable(),
  basis: z.enum(DATE_BASES),
});
export type SourceDate = z.infer<typeof sourceDateSchema>;

export const ACCESS_STATES = ["normal", "restricted", "deleted"] as const;
export const accessStateSchema = z.enum(ACCESS_STATES);
export type AccessState = z.infer<typeof accessStateSchema>;

export const accessFields = {
  access_state: accessStateSchema,
  access_changed_at: instantSchema.nullable(),
  access_audit_id: idSchema("aud").nullable(),
};

export const ROLES = ["viewer", "editor", "operator", "admin"] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export function roleAtLeast(role: Role, required: Role): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(required);
}

export const msRangeSchema = z
  .strictObject({ start_ms: z.number().int().nonnegative(), end_ms: z.number().int().positive() })
  .refine((r) => r.start_ms < r.end_ms, "start_ms must be before end_ms");
export type MsRange = z.infer<typeof msRangeSchema>;

export const STAGES = ["discover", "download", "archive", "transcribe", "speakers", "ready"] as const;
export const stageSchema = z.enum(STAGES);
export type Stage = z.infer<typeof stageSchema>;

export const READINESS_DIMENSIONS = [
  "original",
  "provenance",
  "language",
  "context",
  "text",
  "attribution",
  "problems",
  "access",
] as const;
export const readinessDimensionSchema = z.enum(READINESS_DIMENSIONS);
export const DIMENSION_RESULTS = ["passed", "blocked", "not_run", "inapplicable"] as const;
export const dimensionResultSchema = z.enum(DIMENSION_RESULTS);
export const READINESS = ["draft", "eligible"] as const;
export const readinessSchema = z.enum(READINESS);

export const OUTCOME_CLASSES = [
  "success",
  "challenge",
  "rate_limited",
  "quota_exhausted",
  "auth_failure",
  "credentials_unavailable",
  "member_only",
  "geo_restricted",
  "expired",
  "deleted",
  "not_found",
  "truncated",
  "corrupt",
  "preview_only",
  "timeout",
  "network_error",
  "unprocessable",
  "provider_error",
] as const;
export const outcomeClassSchema = z.enum(OUTCOME_CLASSES);
export type OutcomeClass = z.infer<typeof outcomeClassSchema>;

export const CONTEXT_STATUSES = ["complete", "partial", "unavailable_original", "unresolved"] as const;
export const contextStatusSchema = z.enum(CONTEXT_STATUSES);

export const confidenceLabelSchema = z.enum([
  "model estimate",
  "provider score",
  "calibrated probability",
  "unavailable",
]);

export const confidenceSchema = z.strictObject({
  value: z.number().min(0).max(1).nullable(),
  target: z.string().min(1),
  kind: z.enum(["provider_score", "model_estimate", "calibrated_probability", "unavailable"]),
  method_ref: z.string().min(1),
  calibration: z.strictObject({
    status: z.enum(["uncalibrated", "calibrated", "out_of_scope", "stale", "unavailable"]),
    version: z.string().nullable(),
    population_ref: z.string().nullable(),
  }),
  raw_signal_refs: z.array(z.string()),
  unavailable_reason: z.string().nullable(),
});
export type Confidence = z.infer<typeof confidenceSchema>;

export const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);
